import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableCell,
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
  TextField,
  CircularProgress,
  TablePagination,
  TableSortLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { AdminDeliveriesTableRow } from "./AdminDeliveriesTableRow";
import { DeliveryLocationModel } from "../../../models/DeliveryLocationModel";

const readableSortColor = "rgba(0, 0, 0, 0.87)";
const readableSortIconColor = "rgba(0, 0, 0, 0.54)";
const sortLabelStyle: React.CSSProperties = { color: readableSortColor };

const useStyles = makeStyles({
  sortIcon: {
    color: `${readableSortIconColor} !important`,
  },
});

export const AdminDeliveriesTable: React.FC = () => {
  const classes = useStyles();
  const [deliveryData, setDeliveryData] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    (async () => {
      console.log("Fetching page", page, "rowsPerPage", rowsPerPage, "search", search);
      setLoading(true);
      try {
        const response = await DeliveryLocationModel.getAll(
          page,
          rowsPerPage,
          search,
          sortBy,
          sortOrder
        );
        setDeliveryData(response.data || []);
        setTotalCount(response.total);
      } catch {
        setDeliveryData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, rowsPerPage, search, sortBy, sortOrder]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    setPage(0);

    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const sortLabelClasses = {
    icon: classes.sortIcon,
  };

  // Reset page to 0 on new search
  useEffect(() => {
    setPage(0);
  }, [search]);

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
        <TextField
          id="search-deliveries"
          label="Search Deliveries"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth={false}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="orders table">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell align="center">Address</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === "deliveryCost"}
                  classes={sortLabelClasses}
                  direction={sortBy === "deliveryCost" ? sortOrder : "desc"}
                  onClick={() => handleSort("deliveryCost")}
                  style={sortLabelStyle}
                >
                  Price
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Length (mins)</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === "deliveryAt"}
                  classes={sortLabelClasses}
                  direction={sortBy === "deliveryAt" ? sortOrder : "desc"}
                  onClick={() => handleSort("deliveryAt")}
                  style={sortLabelStyle}
                >
                  Delivery at
                </TableSortLabel>
              </TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : deliveryData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              deliveryData.map((data: any, index: number) => (
                <AdminDeliveriesTableRow location={data} key={index} />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        />
      </TableContainer>
    </>
  );
};
